import { useEffect, useMemo } from "react";

import { useAccessibleItems } from "app/components/React/QuickSearch/pageData/useAccessibleItems";

import { ONBOARDING_AGENT_QUICK_ACTION_TYPE } from "../onboardingAgent.types";
import { ONBOARDING_AGENT_SECONDARY_REVEAL_INTERVAL_MS } from "../onboardingAgentConstants";

import { OnboardingAgentAssistantExploreNext } from "./OnboardingAgentAssistantExploreNext";
import { OnboardingAgentAssistantFollowUps } from "./OnboardingAgentAssistantFollowUps";
import { computeAssistantSecondaryVisibleCounts } from "./helpers/computeAssistantSecondaryVisibleCounts";
import { renderAssistantText } from "./helpers/OnboardingAgentAssistantMessage.helpers";
import type {
    OnboardingAgentAssistantExploreNextItem,
    OnboardingAgentAssistantQuickAction,
} from "./OnboardingAgentAssistantMessage.types";
import { OnboardingAgentAssistantQuickActions } from "./OnboardingAgentAssistantQuickActions";
import { OnboardingAgentAssistantSuggestedActions } from "./OnboardingAgentAssistantSuggestedActions";
import { OnboardingAgentReplyFeedbackToolbar } from "./OnboardingAgentReplyFeedbackToolbar";
import { AssistantBodyStack, AssistantContentStack, AssistantMessageRoot } from "./styles";
import type { StaggeredRevealPhase } from "./hooks/useStaggeredSecondaryReveal";
import { useStaggeredSecondaryReveal } from "./hooks/useStaggeredSecondaryReveal";
import { useStreamedAssistantText } from "./hooks/useStreamedAssistantText";

export type OnboardingAgentAssistantMessageProps = {
    text: string;
    messageIndex: number;
    copyPlainText: string;
    threadId?: string;
    exploreNextItems?: readonly OnboardingAgentAssistantExploreNextItem[];
    followUpQuestions?: readonly string[];
    quickActions?: readonly OnboardingAgentAssistantQuickAction[];
    suggestedActions?: readonly string[];
    isFollowUpDisabled?: boolean;
    shouldStream?: boolean;
    onStreamComplete?: () => void;
    onAskFollowUp: (question: string) => void;
    onInAppQuickLinkLanding?: (pageTitle: string) => void;
};

const NOOP = (): void => undefined;

const getExploreNextItemUiRouterState = (item: OnboardingAgentAssistantExploreNextItem): string | undefined =>
    item.uiRouterState;

export const OnboardingAgentAssistantMessage = ({
    text,
    messageIndex,
    copyPlainText,
    threadId,
    exploreNextItems,
    followUpQuestions,
    quickActions,
    suggestedActions,
    isFollowUpDisabled = false,
    shouldStream = false,
    onStreamComplete,
    onAskFollowUp,
    onInAppQuickLinkLanding,
}: OnboardingAgentAssistantMessageProps) => {
    const accessibleExploreNext = useAccessibleItems(exploreNextItems ?? [], getExploreNextItemUiRouterState);
    const followUps = followUpQuestions ?? [];
    const suggestions = suggestedActions ?? [];
    const visibleQuickActions = useMemo(
        () => (quickActions ?? []).filter((action) => action.type !== ONBOARDING_AGENT_QUICK_ACTION_TYPE.navigate),
        [quickActions],
    );

    const hasFeedbackToolbar = text.length > 0;

    const { visibleText, isStreaming: isStreamingText } = useStreamedAssistantText(text, shouldStream, NOOP);

    let secondaryPhase: StaggeredRevealPhase;
    if (!shouldStream) {
        secondaryPhase = "complete";
    } else if (isStreamingText) {
        secondaryPhase = "hidden";
    } else {
        secondaryPhase = "revealing";
    }

    const totalSecondaryItems =
        accessibleExploreNext.length +
        visibleQuickActions.length +
        followUps.length +
        suggestions.length +
        (hasFeedbackToolbar ? 1 : 0);

    const revealedSecondaryCount = useStaggeredSecondaryReveal(
        totalSecondaryItems,
        secondaryPhase,
        ONBOARDING_AGENT_SECONDARY_REVEAL_INTERVAL_MS,
    );

    const {
        exploreNextVisibleCount,
        quickActionsVisibleCount,
        followUpsVisibleCount,
        suggestionsVisibleCount,
        isFeedbackToolbarVisible,
    } = computeAssistantSecondaryVisibleCounts({
        exploreNextLength: accessibleExploreNext.length,
        visibleQuickActionsLength: visibleQuickActions.length,
        followUpsLength: followUps.length,
        suggestionsLength: suggestions.length,
        hasFeedbackToolbar,
        revealedSecondaryCount,
    });

    useEffect(() => {
        if (!shouldStream) {
            return;
        }
        if (isStreamingText) {
            return;
        }
        if (revealedSecondaryCount < totalSecondaryItems) {
            return;
        }
        onStreamComplete?.();
    }, [shouldStream, isStreamingText, revealedSecondaryCount, totalSecondaryItems, onStreamComplete]);

    return (
        <AssistantMessageRoot data-automation={`onboarding-agent-assistant-message-${messageIndex}`}>
            <AssistantContentStack>
                {visibleText ? (
                    <AssistantBodyStack>{renderAssistantText(visibleText, threadId)}</AssistantBodyStack>
                ) : null}

                {exploreNextVisibleCount > 0 ? (
                    <OnboardingAgentAssistantExploreNext
                        messageIndex={messageIndex}
                        items={accessibleExploreNext.slice(0, exploreNextVisibleCount)}
                        threadId={threadId}
                        onInAppQuickLinkLanding={onInAppQuickLinkLanding}
                    />
                ) : null}

                {quickActionsVisibleCount > 0 ? (
                    <OnboardingAgentAssistantQuickActions
                        messageIndex={messageIndex}
                        actions={visibleQuickActions.slice(0, quickActionsVisibleCount)}
                        threadId={threadId}
                    />
                ) : null}

                {followUpsVisibleCount > 0 ? (
                    <OnboardingAgentAssistantFollowUps
                        messageIndex={messageIndex}
                        questions={followUps.slice(0, followUpsVisibleCount)}
                        isDisabled={isFollowUpDisabled}
                        onAskFollowUp={onAskFollowUp}
                    />
                ) : null}

                {suggestionsVisibleCount > 0 ? (
                    <OnboardingAgentAssistantSuggestedActions
                        messageIndex={messageIndex}
                        actions={suggestions.slice(0, suggestionsVisibleCount)}
                    />
                ) : null}
            </AssistantContentStack>

            {isFeedbackToolbarVisible ? (
                <OnboardingAgentReplyFeedbackToolbar
                    messageIndex={messageIndex}
                    copyPlainText={copyPlainText}
                    threadId={threadId}
                />
            ) : null}
        </AssistantMessageRoot>
    );
};
